import React, { useState, useEffect } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useUser } from '@supabase/auth-helpers-react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  Grid,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import FaEditIcon from '@mui/icons-material/Edit';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useTestCases } from '../../hooks/useTestCases';

const theme = createTheme({
  palette: {
    mode: 'light'
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
          fontSize: '0.875rem'
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          padding: '4px 8px',
          border: 'none',
          '&.Mui-selected': {
            backgroundColor: 'transparent'
          }
        }
      }
    }
  }
});

const statusIcons = {
  passed: <CheckCircleIcon color="success" />,
  failed: <ErrorIcon color="error" />,
  pending: <PendingIcon color="warning" />,
  blocked: <BlockIcon color="disabled" />
};

const priorityColors = {
  high: 'error',
  medium: 'warning',
  low: 'success'
};

const TestCaseManagement = () => {
  const [menuHierarchy, setMenuHierarchy] = useState([]);
  const [expanded, setExpanded] = useState(['1']);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [selectedMenuGroup, setSelectedMenuGroup] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);

  const { fetchTestCases, updateTestCase, addTestCase, loading, error } = useTestCases();
  const supabase = useSupabaseClient();
  const user = useUser();

  const fetchMenuHierarchy = async () => {
    console.log('Fetching menu hierarchy...');
    try {
      const { data: testCases, error: hierarchyError } = await supabase
        .from('test_cases_with_hierarchy')
        .select('*')
        .order('group_order')
        .order('item_order')
        .order('priority', { ascending: false });
      
      if (hierarchyError) {
        console.error('Error fetching menu hierarchy:', hierarchyError);
        return [];
      }

      console.log('Raw test cases:', testCases);

      if (!testCases || testCases.length === 0) {
        console.log('No test cases found');
        return [];
      }

      // Group the data by menu_group and menu_item
      const grouped = testCases.reduce((acc, testCase) => {
        const group = acc.find(g => g.menu_group_id === testCase.menu_group_id);
        if (group) {
          const item = group.items.find(i => i.menu_item_id === testCase.menu_item_id);
          if (item) {
            item.test_cases.push(testCase);
            if (testCase.status === 'passed') item.passed++;
            else if (testCase.status === 'failed') item.failed++;
            else if (testCase.status === 'pending') item.pending++;
            else if (testCase.status === 'blocked') item.blocked++;
          } else {
            group.items.push({
              menu_item_id: testCase.menu_item_id,
              menu_item_name: testCase.menu_item_name,
              item_order: testCase.item_order,
              test_cases: [testCase],
              passed: testCase.status === 'passed' ? 1 : 0,
              failed: testCase.status === 'failed' ? 1 : 0,
              pending: testCase.status === 'pending' ? 1 : 0,
              blocked: testCase.status === 'blocked' ? 1 : 0
            });
          }
        } else {
          acc.push({
            menu_group_id: testCase.menu_group_id,
            menu_group_name: testCase.menu_group_name,
            group_order: testCase.group_order,
            items: [{
              menu_item_id: testCase.menu_item_id,
              menu_item_name: testCase.menu_item_name,
              item_order: testCase.item_order,
              test_cases: [testCase],
              passed: testCase.status === 'passed' ? 1 : 0,
              failed: testCase.status === 'failed' ? 1 : 0,
              pending: testCase.status === 'pending' ? 1 : 0,
              blocked: testCase.status === 'blocked' ? 1 : 0
            }]
          });
        }
        return acc;
      }, []);

      console.log('Grouped hierarchy:', grouped);
      return grouped;
    } catch (err) {
      console.error('Error in fetchMenuHierarchy:', err);
      return [];
    }
  };

  const loadData = async () => {
    console.log('Loading data...');
    const hierarchyData = await fetchMenuHierarchy();
    console.log('Final hierarchy data:', hierarchyData);
    setMenuHierarchy(hierarchyData);
  };

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  useEffect(() => {
    const storedTestCaseId = localStorage.getItem('selectedTestCaseId');
    if (storedTestCaseId && menuHierarchy.length > 0) {
      const group = menuHierarchy.find(g => 
        g.items.some(item => 
          item.test_cases.some(test => test.id === storedTestCaseId)
        )
      );
      if (group) {
        const item = group.items.find(item => 
          item.test_cases.some(test => test.id === storedTestCaseId)
        );
        if (item) {
          const testCase = item.test_cases.find(test => test.id === storedTestCaseId);
          setSelectedTestCase(testCase);
          setSelectedNode(`test-${storedTestCaseId}`);
        }
      }
    }
  }, [menuHierarchy]);

  const handleNodeSelect = (nodeId) => {
    const [type, id] = nodeId.split('-');
    if (type === 'test') {
      const group = menuHierarchy.find(g => 
        g.items.some(item => 
          item.test_cases.some(test => test.id === id)
        )
      );
      if (group) {
        const item = group.items.find(item => 
          item.test_cases.some(test => test.id === id)
        );
        if (item) {
          const testCase = item.test_cases.find(test => test.id === id);
          setSelectedTestCase(testCase);
          localStorage.setItem('selectedTestCaseId', id);
        }
      }
    } else {
      setSelectedTestCase(null);
      localStorage.removeItem('selectedTestCaseId');
    }
    setSelectedNode(nodeId);
  };

  const handleStatusChange = async (testCase, newStatus) => {
    try {
      await updateTestCase(testCase.id, {
        status: newStatus,
        last_tested_at: new Date().toISOString(),
        last_tested_by: user.id
      });
      await loadData();
    } catch (error) {
      console.error('Error updating test case status:', error);
    }
  };

  const getGroupStats = (group) => {
    return group.items.reduce((acc, item) => ({
      passed: acc.passed + item.passed,
      failed: acc.failed + item.failed,
      pending: acc.pending + item.pending,
      blocked: acc.blocked + item.blocked
    }), { passed: 0, failed: 0, pending: 0, blocked: 0 });
  };

  const getStatusIcon = (status, testCase) => {
    const iconProps = {
      fontSize: 'small',
      sx: { cursor: 'pointer' },
      onClick: (e) => {
        e.stopPropagation();
        setSelectedTestCase(testCase);
      }
    };

    switch (status) {
      case 'passed':
        return <CheckCircleIcon color="success" {...iconProps} />;
      case 'failed':
        return <ErrorIcon color="error" {...iconProps} />;
      case 'pending':
        return <PendingIcon color="warning" {...iconProps} />;
      case 'blocked':
        return <BlockIcon color="disabled" {...iconProps} />;
      default:
        return null;
    }
  };

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleSaveTestCase = async (event) => {
    event.preventDefault();
    try {
      if (selectedTestCase.id) {
        await updateTestCase(selectedTestCase.id, selectedTestCase);
      } else {
        await addTestCase(selectedTestCase);
      }
      setIsEditing(false);
      await loadData();
    } catch (error) {
      console.error('Error saving test case:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    const currentTestCase = menuHierarchy.find(g => 
      g.items.some(item => 
        item.test_cases.some(test => test.id === selectedTestCase.id)
      )
    );
    if (currentTestCase) {
      const item = currentTestCase.items.find(item => 
        item.test_cases.some(test => test.id === selectedTestCase.id)
      );
      if (item) {
        const testCase = item.test_cases.find(test => test.id === selectedTestCase.id);
        setSelectedTestCase(testCase);
      }
    }
  };

  const handleAddTestCase = (menuGroupId, menuItemId) => {
    // Find the menu group and item
    const menuGroup = menuHierarchy.find(g => g.menu_group_id === menuGroupId);
    const menuItem = menuGroup?.items.find(i => i.menu_item_id === menuItemId);
    
    if (menuGroup && menuItem) {
      setSelectedMenuGroup(menuGroup);
      setSelectedMenuItem(menuItem);
      setSelectedTestCase({
        menu_group_id: menuGroupId,
        menu_item_id: menuItemId,
        menu_group_name: menuGroup.menu_group_name,
        menu_item_name: menuItem.menu_item_name,
        feature_area: '',
        test_name: '',
        description: '',
        steps: [''],
        expected_result: '',
        priority: 'medium',
        status: 'pending'
      });
      setIsEditing(true);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ height: '100%', width: '100%', p: 2 }}>
        {/* Breadcrumb */}
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Members & Groups &gt; Manage Groups &gt; Add Members to Group
        </Typography>

        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Test Cases by Menu</Typography>
        </Box>

        {/* Main Content */}
        <Box sx={{ display: 'flex', height: 'calc(100vh - 140px)', width: '100%', gap: 2 }}>
          {/* Left side - TreeView */}
          <Box sx={{ width: '40%', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <SimpleTreeView
                aria-label="test case hierarchy"
                defaultExpanded={['1']}
                selected={selectedNode}
                onNodeSelect={(event, nodeId) => handleNodeSelect(nodeId)}
                onNodeToggle={(event, nodeIds) => setExpanded(nodeIds)}
                sx={{ flexGrow: 1, maxWidth: 400, overflowY: 'auto' }}
              >
                {menuHierarchy.map((group) => (
                  <TreeItem
                    key={`group-${group.menu_group_id}`}
                    nodeId={`group-${group.menu_group_id}`}
                    itemId={`group-${group.menu_group_id}`}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1">{group.menu_group_name}</Typography>
                        <Box>
                          <Chip 
                            size="small"
                            label={getGroupStats(group).passed}
                            color="success"
                            sx={{ mr: 0.5 }}
                          />
                          <Chip 
                            size="small"
                            label={getGroupStats(group).failed}
                            color="error"
                            sx={{ mr: 0.5 }}
                          />
                          <Chip 
                            size="small"
                            label={getGroupStats(group).pending}
                            color="warning"
                            sx={{ mr: 0.5 }}
                          />
                          <Chip 
                            size="small"
                            label={getGroupStats(group).blocked}
                            color="default"
                            sx={{ mr: 0.5 }}
                          />
                        </Box>
                      </Box>
                    }
                  >
                    {group.items.map((item) => (
                      <TreeItem
                        key={`item-${item.menu_item_id}`}
                        nodeId={`item-${item.menu_item_id}`}
                        itemId={`item-${item.menu_item_id}`}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body1">{item.menu_item_name}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box>
                                <Chip 
                                  size="small"
                                  label={item.passed}
                                  color="success"
                                  sx={{ mr: 0.5 }}
                                />
                                <Chip 
                                  size="small"
                                  label={item.failed}
                                  color="error"
                                  sx={{ mr: 0.5 }}
                                />
                                <Chip 
                                  size="small"
                                  label={item.pending}
                                  color="warning"
                                  sx={{ mr: 0.5 }}
                                />
                                <Chip 
                                  size="small"
                                  label={item.blocked}
                                  color="default"
                                  sx={{ mr: 0.5 }}
                                />
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddTestCase(group.menu_group_id, item.menu_item_id);
                                }}
                                sx={{ ml: 1 }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        }
                      >
                        {item.test_cases.map((testCase) => (
                          <TreeItem
                            key={`test-${testCase.id}`}
                            nodeId={`test-${testCase.id}`}
                            itemId={`test-${testCase.id}`}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2">{testCase.test_name}</Typography>
                                {getStatusIcon(testCase.status, testCase)}
                              </Box>
                            }
                          />
                        ))}
                      </TreeItem>
                    ))}
                  </TreeItem>
                ))}
              </SimpleTreeView>
            )}
          </Box>

          {/* Right side - Test Case Details */}
          <Box sx={{ width: '60%', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            {selectedTestCase ? (
              <Box component={isEditing ? "form" : "div"} onSubmit={handleSaveTestCase}>
                {/* Title and Edit/Save buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {isEditing ? (
                    <>
                      <TextField
                        fullWidth
                        label="Test Name"
                        value={selectedTestCase.test_name || ''}
                        onChange={(e) => setSelectedTestCase(prev => ({ ...prev, test_name: e.target.value }))}
                        sx={{ mr: 2 }}
                      />
                      <Box>
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          sx={{ mr: 1 }}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6">{selectedTestCase.test_name}</Typography>
                      <IconButton
                        color="primary"
                        onClick={handleStartEdit}
                        size="large"
                      >
                        <FaEditIcon />
                      </IconButton>
                    </>
                  )}
                </Box>

                {/* Breadcrumb */}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {selectedTestCase.menu_group_name} &gt; {selectedTestCase.menu_item_name} &gt; {selectedTestCase.test_name}
                </Typography>

                {/* Headers for Priority and Status */}
                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">Priority</Typography>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                </Box>

                {/* Priority and Status */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Box>
                    {isEditing ? (
                      <FormControl>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          value={selectedTestCase.priority || 'medium'}
                          onChange={(e) => setSelectedTestCase(prev => ({ ...prev, priority: e.target.value }))}
                          label="Priority"
                          size="small"
                        >
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="low">Low</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip 
                        label={selectedTestCase.priority}
                        color={priorityColors[selectedTestCase.priority]}
                        size="small"
                      />
                    )}
                  </Box>
                  <Box>
                    {isEditing ? (
                      <FormControl>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={selectedTestCase.status || 'pending'}
                          onChange={(e) => setSelectedTestCase(prev => ({ ...prev, status: e.target.value }))}
                          label="Status"
                          size="small"
                        >
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="passed">Passed</MenuItem>
                          <MenuItem value="failed">Failed</MenuItem>
                          <MenuItem value="blocked">Blocked</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon(selectedTestCase.status, selectedTestCase)}
                        <Typography>{selectedTestCase.status}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Feature Area */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Feature Area</Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      value={selectedTestCase.feature_area || ''}
                      onChange={(e) => setSelectedTestCase(prev => ({ ...prev, feature_area: e.target.value }))}
                    />
                  ) : (
                    <Typography>{selectedTestCase.feature_area}</Typography>
                  )}
                </Box>

                {/* Expected Result */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Expected Result</Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      value={selectedTestCase.expected_result || ''}
                      onChange={(e) => setSelectedTestCase(prev => ({ ...prev, expected_result: e.target.value }))}
                    />
                  ) : (
                    <Typography>{selectedTestCase.expected_result}</Typography>
                  )}
                </Box>

                {/* Description */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Description</Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      value={selectedTestCase.description || ''}
                      onChange={(e) => setSelectedTestCase(prev => ({ ...prev, description: e.target.value }))}
                    />
                  ) : (
                    <Typography>{selectedTestCase.description}</Typography>
                  )}
                </Box>

                {/* Test Steps with reduced spacing */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Test Steps</Typography>
                  {isEditing ? (
                    <>
                      {(selectedTestCase.steps || ['']).map((step, index) => (
                        <Box key={index} sx={{ mb: 0.5, display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label={`Step ${index + 1}`}
                            value={step}
                            onChange={(e) => {
                              const newSteps = [...(selectedTestCase.steps || [''])];
                              newSteps[index] = e.target.value;
                              setSelectedTestCase(prev => ({ ...prev, steps: newSteps }));
                            }}
                          />
                          {index > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newSteps = [...(selectedTestCase.steps || [''])];
                                newSteps.splice(index, 1);
                                setSelectedTestCase(prev => ({ ...prev, steps: newSteps }));
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newSteps = [...(selectedTestCase.steps || ['']), ''];
                          setSelectedTestCase(prev => ({ ...prev, steps: newSteps }));
                        }}
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        Add Step
                      </Button>
                    </>
                  ) : (
                    <List dense disablePadding>
                      {(selectedTestCase.steps || []).map((step, index) => (
                        <ListItem key={index} sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Typography color="textSecondary">{index + 1}.</Typography>
                          </ListItemIcon>
                          <ListItemText primary={step} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="textSecondary">Select a test case to view details</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TestCaseManagement;
